import { z } from "zod";
import { CHECKOFF_OPERATION_TRANSPORT_MAX_UTF8_BYTES, CANONICAL_UNITS, GROCERY_PACKAGE_UNITS } from "@batch-and-bowl/shared";
import { mealTypeEnum } from "./recipe";
import { PROTEIN_TAG_VALUES } from "./recipe-tags";

export * from "./recipe-tags";
export * from "./finalize-recipe-tags";
export * from "./recipe-cuisine";

/**
 * Canonicalize a dietary restriction / tag string into the underscore tag
 * vocabulary used by recipe.tags: lowercase, trim, hyphens and whitespace
 * runs -> single underscore (#69 F1).
 */
export function canonicalizeDietaryTag(value: string): string {
  // Collapse any run of hyphens, underscores, and whitespace into one "_"
  // so mixed separators ("gluten - free", "gluten_-free") all normalize
  // to the underscore tag vocabulary (ported from main's ffd9e05).
  return value.trim().toLowerCase().replace(/[-_\s]+/g, "_");
}

/** The full dietary-restriction vocabulary offered by the product. */
export const DIETARY_RESTRICTION_TAGS = [
  "vegetarian",
  "vegan",
  "pescatarian",
  "gluten_free",
  "dairy_free",
  "keto",
  "low_carb",
  "paleo",
] as const;
export type DietaryRestrictionTag = (typeof DIETARY_RESTRICTION_TAGS)[number];

/**
 * Onboarding v2 (issue #370, spec §6): the canonical default for which meal
 * types a generated plan should include. NOT applied at this validation
 * layer -- an omitted `mealTypeDefaults` means "leave unchanged"; new rows
 * get this value from the DB column default (schema.prisma + migration),
 * and this constant exists so tests and Track A2's generator defaulting
 * reference one source of truth instead of re-declaring the literal.
 * Matches the pre-onboarding-v2 implicit behavior (breakfast, lunch, and
 * dinner planned; snacks off by default -- generation supports snack slots
 * as of #371, but they stay opt-in via this default).
 */
export const MEAL_TYPE_DEFAULTS_DEFAULT = {
  breakfast: true,
  lunch: true,
  dinner: true,
  snacks: false,
} as const;

/** The only valid dinner-nights-per-week counts (issue #370, spec §6). */
export const DINNER_NIGHTS_OPTIONS = [3, 5, 7] as const;

/**
 * Onboarding v3 child 2 (issue #827, epic #821): the weeknight-pace dial's
 * closed vocabulary, matching the Prisma `WeeknightPace` enum exactly.
 * `null` (absence) means "today's behaviour" == `balanced` -- see
 * DECISIONS.md DECISION-022 -- so this list never includes null itself;
 * null-ness is handled by `.nullable()` on the Zod field, not a 4th value
 * here.
 */
export const WEEKNIGHT_PACE_VALUES = ["quick", "balanced", "relaxed"] as const;
export type WeeknightPace = (typeof WEEKNIGHT_PACE_VALUES)[number];

/**
 * Batch & Bowl-owned preference fields only. `.strict()` is a security
 * boundary: it rejects auth-critical User fields (id, email, emailVerified,
 * subscriptionTier, createdAt, updatedAt, lastLoginAt) so a client can never
 * smuggle a privilege escalation through this endpoint's body.
 */
export const userPreferencesSchema = z
  .object({
    dietaryRestrictions: z
      .array(
        z
          .string()
          .transform(canonicalizeDietaryTag)
          .pipe(z.enum(DIETARY_RESTRICTION_TAGS))
      )
      .default([]),
    // Capped at 50 terms / 100 chars each (final-review Finding 1): without
    // a bound, PUT /preferences drives resolveAllergyTerms' O(terms x
    // vocabulary) Levenshtein scoring over an attacker-controlled array size
    // -- an uncapped ~99KB body (still under express.json()'s 100KB default)
    // measured ~5.7s of blocking CPU. The same cap is applied to
    // dislikedIngredients/preferredCuisines/acknowledgedUnmatchedAllergies
    // for consistency, even though only `allergies` drives the expensive
    // resolution path.
    allergies: z.array(z.string().min(1).max(100)).max(50).default([]),
    // Request-shape consent only (Task 8 / "penuts hole" fix): terms the
    // client is explicitly asking the server to accept even though they
    // didn't resolve against the known allergen/ingredient vocabulary. The
    // route never persists this field -- it's consumed once per request to
    // decide whether to 400 on unmatched allergy terms.
    acknowledgedUnmatchedAllergies: z.array(z.string().min(1).max(100)).max(50).default([]),
    householdSize: z.number().min(1).max(12),
    // Onboarding v2 (issue #370, spec §6): the household split serving-size
    // math will move to. `.optional()` (no `.default()`), matching
    // `preferredZip` below: absent = leave the stored value unchanged
    // (PUT /users/preferences skips undefined keys in the Prisma update --
    // see routes/users.ts), NOT "reset to a generic default". That keeps
    // every existing caller that doesn't yet send these fields working
    // unchanged during the transition -- the web Settings/Preferences
    // forms, the mobile shared-boundary smoke test
    // (apps/mobile/__tests__/shared-boundary.test.ts), and every other
    // PUT /users/preferences caller that predates this field. New rows get
    // their actual defaults from the DB column (schema.prisma), not here.
    householdAdults: z.number().int().min(0).max(8).optional(),
    householdKids: z.number().int().min(0).max(8).optional(),
    skillLevel: z.enum(["beginner", "intermediate", "advanced"]),
    budgetRange: z.enum(["budget", "moderate", "flexible"]),
    preferredCuisines: z.array(z.string().min(1).max(100)).max(50).default([]),
    dislikedIngredients: z.array(z.string().min(1).max(100)).max(50).default([]),
    // Onboarding v3 child 1 (issue #826, epic #821): protein favourites --
    // a scorer/candidate-menu bonus only, never a filter. Restricted to the
    // closed PROTEIN_TAG_VALUES vocabulary (same enum-of-a-readonly-array
    // pattern as proteinFilterSchema below); duplicates are rejected rather
    // than silently de-duplicated, same posture as proteinFilterSchema.
    // Avoided proteins are NOT here -- they're written into
    // dislikedIngredients above as a protein value (see DECISIONS.md).
    preferredProteins: z
      .array(z.enum(PROTEIN_TAG_VALUES as [string, ...string[]]))
      .max(8)
      .refine((values) => new Set(values).size === values.length, {
        message: "duplicate protein values",
      })
      .optional(),
    // Grocery ordering (issue #322): default zip for retailer store selection.
    // Optional = leave unchanged; null = clear. 5-digit US zip only — this
    // feeds retailer store-locator APIs (e.g. Kroger filter.zipCode.near),
    // which are US-format.
    preferredZip: z
      .string()
      .regex(/^\d{5}$/, "must be a 5-digit US zip code")
      .nullable()
      .optional(),
    // Onboarding v2 (issue #370, spec §6): persisted onboarding-time default
    // for which meal types a generated plan should include. Distinct from
    // createMealPlanSchema's per-call `includedMeals` below -- that schema
    // accepts `snacks` as of #371 (per-call override still wins; this is
    // only the stored default). `.optional()`, same leave-unchanged semantics
    // as householdAdults/householdKids above -- when sent, every one of the
    // four keys is required (nested `.strict()`, no partial merge).
    mealTypeDefaults: z
      .object({
        breakfast: z.boolean(),
        lunch: z.boolean(),
        dinner: z.boolean(),
        snacks: z.boolean(),
      })
      .strict()
      .optional(),
    dinnerNights: z.union([z.literal(3), z.literal(5), z.literal(7)]).optional(),
    plainOptionEachNight: z.boolean().optional(),
    fulfillmentPreference: z.enum(["pickup", "delivery", "self"]).optional(),
    // Onboarding v3 child 2 (issue #827, epic #821): weeknight pace dial.
    // `.optional()` = leave unchanged (same posture as dinnerNights above);
    // `.nullable()` so a saved pace can be cleared back to null ("today's
    // behaviour" -- see DECISIONS.md DECISION-022). Restricted to the
    // closed WEEKNIGHT_PACE_VALUES vocabulary, matching the Prisma
    // WeeknightPace enum.
    weeknightPace: z.enum(WEEKNIGHT_PACE_VALUES).nullable().optional(),
    // Meal-prep/leftovers spec §4.5: cookDay (0=Sun..6=Sat), household-level
    // but read from the acting user's own row (not unioned -- see
    // DECISIONS.md), same "leave unchanged when omitted, null clears it"
    // shape as weeknightPace above.
    cookDay: z.number().int().min(0).max(6).nullable().optional(),
    leftoversForLunch: z.boolean().optional(),
  })
  .strict()
  // Onboarding v2 (issue #370, spec §6): the two household counts must be
  // sent together or not at all. With leave-unchanged semantics for each
  // individually optional field, a request setting only one of the two
  // couldn't know what the other would end up being -- which made the
  // >=1-person rule unenforceable and let `householdAdults: 0` alone
  // produce a zero-person household (A1 review finding). Requiring the
  // pair closes that hole and matches the only real writer (the onboarding
  // form always submits both). Runs after `.strict()`'s own key check, so
  // an unknown-key rejection still reports as such rather than being
  // masked by this rule.
  .refine(
    (data) => (data.householdAdults === undefined) === (data.householdKids === undefined),
    {
      message: "householdAdults and householdKids must be sent together",
      path: ["householdAdults"],
    },
  )
  // Onboarding v2 (issue #370, spec §6): a household needs at least one
  // person. Always enforceable now that the counts arrive as a pair.
  .refine(
    (data) =>
      data.householdAdults === undefined ||
      data.householdKids === undefined ||
      data.householdAdults + data.householdKids >= 1,
    {
      message: "household must have at least one person (householdAdults + householdKids)",
      path: ["householdAdults"],
    },
  );

/**
 * The full ingredient-category vocabulary, mirroring the Prisma
 * `IngredientCategory` enum (packages/database/prisma/schema.prisma) -- the
 * single non-database source of truth for "what is a known category name".
 * In-store mode (E10 / ladder item 13) validates `User.aisleOrder` writes
 * against this list so garbage names can never accumulate in the column
 * (reads IGNORE unknown names per the schema comment, so a stray write
 * would not break rendering -- but it would silently rot, which is why
 * writes reject instead).
 */
export const INGREDIENT_CATEGORIES = [
  "produce",
  "dairy",
  "meat",
  "seafood",
  "bakery",
  "pantry",
  "frozen",
  "beverages",
  "condiments",
  "spices",
  "other",
] as const;
export type IngredientCategoryName = (typeof INGREDIENT_CATEGORIES)[number];

/**
 * Body schema for PATCH /users/aisle-order (in-store mode, E10): the user's
 * own store-walk order for the grocery list's aisle groups. `.strict()` per
 * the security-boundary pattern above. Every entry must be a KNOWN category
 * name (see INGREDIENT_CATEGORIES) and may appear at most once -- a
 * duplicate is always a client bug, and letting it through would make the
 * saved order's index lookup ambiguous. An empty array is valid and means
 * "back to the default order" (the schema contract's `[]` state).
 */
export const updateAisleOrderSchema = z
  .object({
    aisleOrder: z
      .array(z.enum(INGREDIENT_CATEGORIES))
      .max(INGREDIENT_CATEGORIES.length)
      .refine((order) => new Set(order).size === order.length, {
        message: "aisleOrder must not contain duplicate categories",
      }),
  })
  .strict();

export type UpdateAisleOrderInput = z.infer<typeof updateAisleOrderSchema>;

/**
 * Body schema for POST /ingredients/resolve-allergies. `.strict()` rejects
 * unrecognized fields (same security-boundary pattern as
 * userPreferencesSchema above); the 30-term cap and per-term max-length (100,
 * matching the `allergies` field above) bound the vocabulary-matching work
 * the resolution service does per request -- an uncapped per-term length
 * reopens the CPU-DoS vector (an ~99KB body driving O(terms × vocabulary)
 * Levenshtein comparisons) the length cap on `allergies` already closed.
 */
export const resolveAllergiesSchema = z
  .object({
    terms: z.array(z.string().min(1).max(100)).max(30),
  })
  .strict();

/**
 * `.strict()` rejects unrecognized fields (same security-boundary pattern
 * as userPreferencesSchema above). Its prior absence is why dead `days` and
 * `snacks` fields could be sent by a client with no validation error at
 * all -- they'd just silently pass through unused. Note `.strict()` only
 * applies to the object it's called on, so the nested `includedMeals`
 * object needs its own -- without it a nested `snacks: true` key sailed
 * through unnoticed (issue #260).
 *
 * Issue #260 rejected `snacks` inside `includedMeals` because generation
 * itself had no snack-slot support -- accepting the key would have silently
 * no-opped it. Track A2 (issue #371) wires snack slots into
 * meal-plan-generator.ts, so `snacks` is now a real, honored key and the
 * nested-strict rejection of it is deliberately REVERSED here. It defaults
 * to `false` (not required) so every existing 3-key caller keeps validating
 * unchanged. `includedMeals` itself is now `.optional()` -- an omitted call
 * falls back to the caller's stored `mealTypeDefaults` (see
 * routes/meal-plans.ts), which this schema can't see, so the default can't
 * live here.
 */
export const createMealPlanSchema = z
  .object({
    startDate: z.string().date(),
    includedMeals: z
      .object({
        breakfast: z.boolean(),
        lunch: z.boolean(),
        dinner: z.boolean(),
        snacks: z.boolean().default(false),
      })
      .strict()
      .optional(),
  })
  .strict();

const candidateCalendarDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "must be a YYYY-MM-DD calendar date")
  .refine(
    (value) => {
      const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
      if (match === null) return false;

      const year = Number(match[1]);
      const month = Number(match[2]);
      const day = Number(match[3]);
      const leapYear = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
      const daysInMonth = [31, leapYear ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
      const maximumDay = daysInMonth[month - 1];

      return maximumDay !== undefined && day >= 1 && day <= maximumDay;
    },
    { message: "must be a real calendar date" },
  );

const candidateIncludedMealsSchema = z
  .object({
    breakfast: z.boolean(),
    lunch: z.boolean(),
    dinner: z.boolean(),
    snacks: z.boolean(),
  })
  .strict();

const candidateSeedSchema = z.number().int().min(0).max(0x7fffffff);
const candidateFingerprintSchema = z
  .string()
  .regex(/^[0-9a-f]{64}$/, "must be a lowercase 64-character hex fingerprint");
const candidateRecipeIdSchema = z.string().min(1);
const candidateMealTypes = ["breakfast", "lunch", "dinner", "snack"] as const;

function uniqueRecipeIdsSchema(max?: number) {
  const ids = z.array(candidateRecipeIdSchema);
  return (max === undefined ? ids : ids.max(max))
    .superRefine((recipeIds, ctx) => {
      const seen = new Set<string>();
      recipeIds.forEach((recipeId, index) => {
        if (seen.has(recipeId)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "recipe IDs must not contain duplicates",
            path: [index],
          });
        }
        seen.add(recipeId);
      });
    });
}

/** Complete Review/commit selection, using singular `snack` by MealType. */
export const selectedRecipeIdsSchema = z
  .object({
    breakfast: uniqueRecipeIdsSchema(7),
    lunch: uniqueRecipeIdsSchema(7),
    dinner: uniqueRecipeIdsSchema(7),
    snack: uniqueRecipeIdsSchema(7),
  })
  .strict()
  .superRefine((selectedRecipeIds, ctx) => {
    const seen = new Map<string, (typeof candidateMealTypes)[number]>();

    for (const mealType of candidateMealTypes) {
      selectedRecipeIds[mealType].forEach((recipeId, index) => {
        const firstMealType = seen.get(recipeId);
        if (firstMealType !== undefined) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `recipe ID is already selected for ${firstMealType}`,
            path: [mealType, index],
          });
        } else {
          seen.set(recipeId, mealType);
        }
      });
    }
  });

/** Optimistic plan identity carried from Review to the authoritative commit. */
export const expectedMealPlanSchema = z
  .object({
    id: z.string().min(1),
    updatedAt: z.string().datetime(),
    slotFingerprint: candidateFingerprintSchema,
  })
  .strict();

/**
 * #813 PR 3: refresh.proteinFilter narrows the candidate pool of a refresh
 * to a closed set of PROTEIN_TAG_VALUES. PROTEIN_TAG_VALUES is a
 * `readonly string[]`, not a literal tuple, so it can't back `z.enum`
 * directly -- a `.refine` against the runtime array (checked/documented
 * closed contract, same "unknown value fails loudly" posture as
 * mealTypeEnum's GET /recipes ?mealType= validation) does the same job.
 * Duplicates are rejected rather than silently de-duplicated: a client that
 * sends the same value twice has a bug worth surfacing, not smoothing over.
 */
const proteinFilterValueSchema = z
  .string()
  .refine((value): value is (typeof PROTEIN_TAG_VALUES)[number] =>
    (PROTEIN_TAG_VALUES as readonly string[]).includes(value), {
    message: `unknown protein value; expected one of: ${PROTEIN_TAG_VALUES.join(", ")}`,
  });

const proteinFilterSchema = z
  .array(proteinFilterValueSchema)
  .max(8)
  .refine((values) => new Set(values).size === values.length, {
    message: "duplicate protein values",
  });

const refreshCandidateMenuSchema = z
  .object({
    mealType: mealTypeEnum,
    // Cumulative browsing history, not one 12-card page. Express's request
    // byte limit bounds input; a page-sized cap prevents catalog exploration.
    presentedRecipeIds: uniqueRecipeIdsSchema(),
    proteinFilter: proteinFilterSchema.optional(),
  })
  .strict();

/** Initial, refresh, and Review requests for the stateless candidate menu. */
export const previewMealPlanSchema = z
  .object({
    startDate: candidateCalendarDateSchema,
    includedMeals: candidateIncludedMealsSchema.optional(),
    replaceExisting: z.boolean().optional(),
    seed: candidateSeedSchema.optional(),
    selectedRecipeIds: selectedRecipeIdsSchema.optional(),
    refresh: refreshCandidateMenuSchema.optional(),
  })
  .strict()
  .superRefine((request, ctx) => {
    if (request.refresh !== undefined) {
      if (request.seed === undefined) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "refresh requires seed", path: ["seed"] });
      }
      if (request.selectedRecipeIds === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "refresh requires the complete selectedRecipeIds map",
          path: ["selectedRecipeIds"],
        });
      }
      return;
    }

    if (request.selectedRecipeIds !== undefined && request.seed === undefined) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Review requires seed", path: ["seed"] });
    }
  });

/** Authoritative candidate-menu commit request. */
export const commitMealPlanSchema = z
  .object({
    commitId: z.string().uuid(),
    startDate: candidateCalendarDateSchema,
    includedMeals: candidateIncludedMealsSchema.optional(),
    seed: candidateSeedSchema,
    mode: z.enum(["create", "fill", "replace"]),
    expectedPlan: expectedMealPlanSchema.nullable(),
    revisionFingerprint: candidateFingerprintSchema,
    selectedRecipeIds: selectedRecipeIdsSchema,
    acknowledgeOpenSlots: z.boolean(),
    confirmReplace: z.boolean(),
  })
  .strict();

/**
 * Body schema for POST /meal-plans/:id/suggestions (Adaptive Week).
 *
 * The request identifies one explicit empty calendar slot. Cuisine is a
 * request-scoped exact filter, not a preference write; the route compares
 * it case-insensitively against Recipe.cuisine and leaves the slot empty on
 * no match. A bounded non-empty string keeps this package independent of the
 * web client's display list while still closing the API validation boundary.
 */
export const suggestMealSchema = z
  .object({
    date: z.string().date(),
    mealType: mealTypeEnum,
    cuisine: z.string().trim().min(1).max(64).optional(),
  })
  .strict();

export type SuggestMealInput = z.infer<typeof suggestMealSchema>;

/** An open-flow history only; preview never saves preference or plan changes. */
export const previewSuggestedMealSchema = suggestMealSchema.extend({
  skippedRecipeIds: z.array(z.string().min(1).max(100))
    .refine((ids) => new Set(ids).size === ids.length, "Duplicate recipe IDs")
    .default([]),
});
export type PreviewSuggestedMealInput = z.infer<typeof previewSuggestedMealSchema>;

/**
 * Body schema for PUT /recipes/:id/rating. `.strict()` rejects unrecognized
 * fields (same security-boundary pattern as the schemas above). `value` is
 * the client-facing thumbs label (see `RatingValue` in
 * packages/shared/src/types/index.ts) -- `null` means "remove my rating".
 */
export const rateRecipeSchema = z
  .object({
    value: z.enum(["up", "down"]).nullable(),
  })
  .strict();

/**
 * Body schema for POST /retailers/:id/match (issue #324). `.strict()` per the
 * security-boundary pattern above.
 */
export const matchRetailerItemsSchema = z
  .object({
    groceryListId: z.string().uuid(),
    /**
     * Track A (#444): line identities to force a re-search for, even when
     * the persisted cache would otherwise call them fresh — "Search again"
     * on an `unavailable`/stale line uses this rather than clearing the
     * whole cache.
     */
    forceLineKeys: z
      .array(
        z
          .object({
            ingredientId: z.string().min(1),
            unit: z.string().min(1),
          })
          .strict(),
      )
      .max(200)
      .optional(),
  })
  .strict();

export type MatchRetailerItemsInput = z.infer<typeof matchRetailerItemsSchema>;

/**
 * Our OWN ceiling on one cart push, not Kroger's -- Kroger's per-request
 * limit is undocumented and, as of W1 slice 2, unmeasured. Exported so the
 * route's error message quotes the same number the schema enforces.
 */
export const KROGER_MAX_CART_SELECTIONS = 100;

/**
 * Body schema for POST /retailers/kroger/handoff (issue #327; extended for
 * live-price capture, issue #382 D1). Bounded like every other list input:
 * ≤KROGER_MAX_CART_SELECTIONS selections, quantity 1–99 (a 21-slot week
 * tops out far below either).
 *
 * `groceryListId` is REQUIRED and NEW IN D1 -- capture cannot identify
 * which grocery list's rows to write onto without it, so a pre-D1 bundle
 * (which never sent it) gets its cart push rejected with 400 (acceptable:
 * dev-stage product, no rolling-deploy tolerance promised for a brand-new
 * required field). It is ALSO the ledger's key (issue #441) -- the same
 * field now does double duty: identifies which GroceryItem rows to capture
 * onto, AND which (groceryList, retailer, upc) ledger rows to diff against.
 * Only the five PER-SELECTION `groceryItemId`/`productId`/`price`/`aisle`/
 * `description` fields are optional, and exist ONLY so the handoff route
 * can (a) persist the confirmed match onto the exact GroceryItem row it
 * came from, and (b) give the ledger an honest "Added so far" label (see
 * routes/retailers.ts) -- none of the five ever reach Kroger's cart API,
 * which still only ever sees `{ upc, quantity }` (now the ledger's computed
 * DELTA quantity, not necessarily this field's raw value). The first four
 * are optional so an older web bundle (mid-rollout) can still push a cart
 * with no price capture at all, same "predates the field" tolerance the
 * review step already documents for `unavailable`; `description` is
 * optional for the same reason plus the plainer one that not every product
 * match carries a description.
 */
export const krogerHandoffSchema = z
  .object({
    groceryListId: z.string().uuid(),
    selections: z
      .array(
        z
          .object({
            upc: z.string().min(1).max(50),
            quantity: z.number().int().min(1).max(99),
            groceryItemId: z.string().uuid().optional(),
            productId: z.string().min(1).max(100).optional(),
            // An absent/zero price is not a price (N4, fix round): Kroger
            // can return `regular: 0` for an out-of-stock item, and .min(0)
            // let that render as a confirmed live $0.00. .positive()
            // rejects zero the same way an absent price already does.
            price: z.number().positive().max(100000).optional(),
            // Cosmetic passenger fields, both sourced verbatim from Kroger's
            // product payload and both display-only. They are TRUNCATED rather
            // than rejected on purpose: a long product description is not a
            // malformed request, and rejecting the body over one would fail an
            // entire cart push -- a real money path -- for a label. Fields that
            // actually drive behaviour (upc, quantity, price) still reject.
            aisle: z.string().max(1000).transform((s) => s.slice(0, 200)).optional(),
            // #441: rides along to the cart ledger's "Added so far" label
            // (RetailerCartItem.description) -- never sent to Kroger.
            description: z
              .string()
              .max(1000)
              .transform((s) => s.slice(0, 200))
              .optional(),
          })
          .strict(),
      )
      .min(1)
      .max(KROGER_MAX_CART_SELECTIONS),
  })
  .strict();

export type KrogerHandoffInput = z.infer<typeof krogerHandoffSchema>;

/**
 * Body schema for PATCH /retailers/:id/selections (#444 second half, closes
 * #416). One row per grocery-list LINE, keyed by `ingredientId`+`unit` --
 * the same stable identity POST /:id/match's cache keys on, never
 * `GroceryItem.id` (unstable across `refreshGroceryList`'s delete+createMany)
 * or array index (#416's root cause: MatchReview held selections by
 * position, so a reordered/inserted line silently inherited someone else's
 * choice).
 *
 * `storeLocationId` is deliberately NOT a field here -- exactly like
 * POST /:id/match, the route derives it from the caller's own retailer
 * connection, so a request body can never point a write at a store the
 * caller isn't actually linked to.
 */
export const updateRetailerSelectionsSchema = z
  .object({
    groceryListId: z.string().uuid(),
    selections: z
      .array(
        z
          .object({
            ingredientId: z.string().min(1),
            unit: z.string().min(1),
            selectedProductId: z.string().min(1).max(100),
            selectedUpc: z.string().min(1).max(50).optional(),
            quantity: z.number().int().min(1).max(99),
            excluded: z.boolean(),
          })
          .strict(),
      )
      .min(1)
      .max(100),
  })
  .strict();

export type UpdateRetailerSelectionsInput = z.infer<
  typeof updateRetailerSelectionsSchema
>;

/**
 * Query schema for GET /retailers/kroger/cart-status (issue #441 wire-up).
 * `.strict()` per the security-boundary pattern used throughout this file --
 * this is a brand-new endpoint with exactly one accepted query key, so an
 * unrecognized one 400s rather than being silently ignored.
 */
export const krogerCartStatusQuerySchema = z
  .object({
    groceryListId: z.string().uuid(),
  })
  .strict();

export type KrogerCartStatusQuery = z.infer<typeof krogerCartStatusQuerySchema>;

/**
 * Body schema for POST /grocery-lists/:mealPlanId/items (issue #374): the
 * grocery-lists router had no manual-add path before this -- every existing
 * item comes from meal-plan-derived consolidation. `.strict()` per the
 * security-boundary pattern above. Bounds match the shape of a real grocery
 * line (unit strings like "clove"/"tablespoon" are short; quantities stay
 * well under 3 digits for anything a household would hand-add).
 */
const checkoffEpochSchema = z.string().regex(/^(0|[1-9]\d*)$/);
const requireCheckoffTransportByteLimit = (value: string, ctx: z.RefinementCtx) => {
  if (
    new TextEncoder().encode(value).byteLength >
    CHECKOFF_OPERATION_TRANSPORT_MAX_UTF8_BYTES
  ) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: "operation ID exceeds 4096 UTF-8 bytes" });
    return;
  }
  if (!value.startsWith("checkoff:v3:")) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: "operation ID must be canonical v3" });
  }
};
const checkoffCausalEnvelopeSchema = z.object({
  checkoffEpoch: checkoffEpochSchema,
  operationId: z.string().superRefine(requireCheckoffTransportByteLimit),
}).strict();
const quantityCausalEnvelopeSchema = z.object({
  checkoffEpoch: checkoffEpochSchema,
  checkoffOperationId: z.string().superRefine(requireCheckoffTransportByteLimit),
}).strict();
export const deleteManualGroceryItemBodySchema = z.union([
  z.object({}).strict(),
  quantityCausalEnvelopeSchema,
]);
export type DeleteManualGroceryItemBodyInput = z.infer<typeof deleteManualGroceryItemBodySchema>;
const manualAddCausalTargetSchema = z.union([
  z.object({ kind: z.literal("existing"), itemId: z.string().uuid(), checkoffEpoch: checkoffEpochSchema, operationId: z.string().superRefine(requireCheckoffTransportByteLimit) }).strict(),
  z.object({ kind: z.literal("create"), expectedAbsent: z.literal(true), operationId: z.string().superRefine(requireCheckoffTransportByteLimit) }).strict(),
]);

const groceryItemAmountSchema = z.object({
  quantity: z.number().positive().max(999),
  unit: z.string().trim().min(1).max(50),
  notes: z.string().trim().max(500).optional(),
});

const krogerProductSelectionSchema = z
  .object({
    retailer: z.literal("kroger"),
    productId: z.string().trim().min(1).max(100),
    searchTerm: z.string().trim().min(2).max(100),
  })
  .strict();

const addGroceryItemBaseSchema = z.union([
  groceryItemAmountSchema
    .extend({ ingredientId: z.string().uuid() })
    .strict(),
  groceryItemAmountSchema
    .extend({
      name: z.string().trim().min(1).max(120),
      category: z.enum(INGREDIENT_CATEGORIES).optional(),
      retailerProduct: krogerProductSelectionSchema.optional(),
    })
    .strict(),
]);

/** Read-only manual-add preparation accepts the write intent, before causality. */
export const prepareManualGroceryItemSchema = addGroceryItemBaseSchema;
export type PrepareManualGroceryItemInput = z.infer<typeof prepareManualGroceryItemSchema>;

export const addGroceryItemSchema = z.union([
  addGroceryItemBaseSchema,
  groceryItemAmountSchema
    .extend({ ingredientId: z.string().uuid(), causalTarget: manualAddCausalTargetSchema })
    .strict(),
  groceryItemAmountSchema
    .extend({ name: z.string().trim().min(1).max(120), category: z.enum(INGREDIENT_CATEGORIES).optional(), retailerProduct: krogerProductSelectionSchema.optional(), causalTarget: manualAddCausalTargetSchema })
    .strict(),
]);

export type AddGroceryItemInput = z.infer<typeof addGroceryItemSchema>;

/** Query contract for the live Kroger catalogue search in issue #387. */
export const krogerProductSearchQuerySchema = z
  .object({ q: z.string().trim().min(2).max(100) })
  .strict();

/** Params for removing only the manual contribution from one list line. */
export const deleteManualGroceryItemParamsSchema = z
  .object({
    mealPlanId: z.string().uuid(),
    itemId: z.string().uuid(),
  })
  .strict();

/**
 * Query schema for GET /grocery-lists/:mealPlanId (Track D, D2, issue #383):
 * `groupBy` picks between the default aisle/category grouping (client-side,
 * unchanged) and server-side by-recipe grouping. `.strict()` per the
 * security-boundary pattern above -- an unrecognized query key or an
 * out-of-enum value both 400 rather than being silently ignored/coerced.
 * The route applies the 'aisle' default itself (not here) so an explicit,
 * valid value and an omitted one are trivially distinguishable if ever
 * needed.
 */
// Fix round (issue #383 adversarial review, finding 6): deliberately NOT
// `.strict()`. This is a QUERY-STRING schema -- callers routinely append
// unrelated params (cache busters, tracking params, a future feature's own
// query key) alongside `groupBy`, and those extras were silently ignored
// before this feature existed. `.strict()` turned that into a hard 400 for
// any request carrying one, which is a real regression for anything
// unrelated to groupBy. Zod's default (non-strict) object behavior already
// does the right thing: unknown keys are stripped from `.data`, never
// rejected -- `groupBy` itself is still validated exactly as before.
export const groceryListGroupByQuerySchema = z.object({
  groupBy: z.enum(['aisle', 'recipe']).optional(),
});

export type GroceryListGroupByQuery = z.infer<typeof groceryListGroupByQuerySchema>;

/**
 * Path-param schema for GET /grocery-lists/:id/savings-suggestions (Track D,
 * D3, issue #384): `:id` is a grocery list id, same convention as every
 * sibling `:id`-scoped route in this file (pin-item/check-item/export).
 * `.strict()` per the security-boundary pattern
 * above -- an unrecognized param key 400s rather than being silently
 * ignored (matches Express's `req.params` always being a plain object here,
 * never carrying extras in practice, but the validation gate should say so
 * explicitly rather than assume it).
 */
export const groceryListIdParamSchema = z
  .object({
    id: z.string().uuid(),
  })
  .strict();

export type GroceryListIdParam = z.infer<typeof groceryListIdParamSchema>;

/**
 * Body schema for PUT /grocery-lists/:id/dismiss-savings-suggestion (Track D,
 * D3, issue #384): "Keep it" durable dismissal of one trim-the-bill
 * suggestion. `.strict()` per the security-boundary pattern above.
 */
export const dismissSavingsSuggestionSchema = z
  .object({
    fromRecipeId: z.string().uuid(),
    toRecipeId: z.string().uuid(),
  })
  .strict();

export type DismissSavingsSuggestionInput = z.infer<
  typeof dismissSavingsSuggestionSchema
>;

/**
 * Body schema for PUT /grocery-lists/:id/check-item. `itemId` was previously
 * unvalidated and used directly as a Prisma `where`, which both allowed a
 * non-boolean `checked` to 500 and let a caller name an item on someone
 * else's list (the route authorized the LIST, then updated by item id alone).
 */
const checkGroceryItemBaseSchema = z.object({
    itemId: z.string().uuid(),
    checked: z.boolean(),
  });

export const checkGroceryItemSchema = z.union([
  checkGroceryItemBaseSchema.strict(),
  checkGroceryItemBaseSchema.extend(checkoffCausalEnvelopeSchema.shape).strict(),
]);

export type CheckGroceryItemInput = z.infer<typeof checkGroceryItemSchema>;

/**
 * Body schema for PUT /grocery-lists/:id/pin-item (fix round, issue #421
 * polish). The route previously read `itemId`/`pinned` straight off
 * `req.body` with hand-rolled `typeof` checks -- functionally similar, but
 * the last hand-written validation on this route while every sibling
 * (check-item, add-item) went through a zod schema. `.strict()` for the
 * same reason as checkGroceryItemSchema: an object `itemId` (e.g.
 * `{ equals: "...", in: [...] }`) would otherwise pass `typeof itemId ===
 * 'string'` as false and 400 anyway under the old hand-rolled check, but
 * `.strict()` plus `z.string().uuid()` closes the same class of
 * filter-injection shape at the schema boundary instead of relying on the
 * route to keep reinventing the check correctly.
 */
export const pinGroceryItemSchema = z
  .object({
    itemId: z.string().uuid(),
    pinned: z.boolean(),
  })
  .strict();

export type PinGroceryItemInput = z.infer<typeof pinGroceryItemSchema>;

/**
 * Body schema for PUT /grocery-lists/:id/remove-item (beta drive, STATE.md
 * F1). Before this the grocery list had no remove at all -- the only way to
 * correct a wrong line was the destructive POST /:mealPlanId/reset. A
 * symmetric TOGGLE, not a DELETE verb: the row must survive to stay
 * restorable (`removed: false` puts it back, free), and a toggle is the
 * house convention every sibling here already follows. `.strict()` for the
 * same filter-injection reason as pinGroceryItemSchema -- an object
 * `itemId` must never reach the scoped `updateMany`'s `where`.
 */
export const removeGroceryItemSchema = z
  .object({
    itemId: z.string().uuid(),
    removed: z.boolean(),
  })
  .strict();

export type RemoveGroceryItemInput = z.infer<typeof removeGroceryItemSchema>;

/**
 * Body schema for PUT /grocery-lists/:id/item-quantity (beta drive, STATE.md
 * F1): the user's explicit "buy this much" override on one line. Bounds
 * deliberately match addGroceryItemSchema's `quantity` above -- the two are
 * the same kind of number about the same kind of line, and letting them
 * drift would mean one path accepts a quantity the other rejects.
 * `.strict()` per the security-boundary pattern above.
 */
// The unit a user picks for a previously-unquantified line is sent to
// PostHog verbatim as `chosenUnit` on grocery_item_quantity_edited (web
// GroceryList.tsx) -- free-text user input must not cross the analytics
// boundary, so this is a closed enum, not a bounded string. Every curated
// ingredient's `commonUnits` value (the picker's "From the recipe" group)
// is already inside this union (verified 2026-09-19 against
// packages/database/src/curated-data/ingredients/*.ts); if a future
// ingredient adds one that isn't, the picker must filter it out rather
// than this schema growing a hole for free text.
const GROCERY_QUANTITY_UNIT_VALUES = [
  ...new Set<string>([...CANONICAL_UNITS, ...GROCERY_PACKAGE_UNITS]),
] as [string, ...string[]];

const setGroceryItemQuantityBaseSchema = z.object({
    itemId: z.string().uuid(),
    quantity: z.number().positive().max(999).nullable(),
    // Grocery line control (#1023): the unit for a line whose stored unit is
    // "". The route refuses it on any other line (UNIT_NOT_EDITABLE) --
    // stored units are never rewritten.
    unit: z.enum(GROCERY_QUANTITY_UNIT_VALUES).optional(),
  });

export const setGroceryItemQuantitySchema = z.union([
  setGroceryItemQuantityBaseSchema.strict(),
  setGroceryItemQuantityBaseSchema.extend(quantityCausalEnvelopeSchema.shape).strict(),
]);

export type SetGroceryItemQuantityInput = z.infer<
  typeof setGroceryItemQuantitySchema
>;

/**
 * Body schema for POST /grocery-lists/:mealPlanId/reset (Track C, Task 5,
 * issue #448): rebuild the list from the plan's CURRENT slots via
 * refreshGroceryList (never regenerateGroceryList -- see the Cross-track
 * contract in docs/superpowers/plans/2026-08-18-track-c-dead-ends.md, which
 * requires the GroceryList row/id to survive a reset). `clearChecked` is
 * optional and additionally unchecks every item -- the "this list has gone
 * stale, start the trip over" case -- distinct from the default rebuild,
 * which preserves purchased state the same way a swap/add-meal refresh does.
 * `.strict()` per the security-boundary pattern above; an empty body (the
 * common case -- just rebuild, don't touch checked state) is valid since
 * `clearChecked` is optional.
 */
const resetCausalOperationSchema = z.string().superRefine(requireCheckoffTransportByteLimit);
const epochsByItemIdSchema = z.record(checkoffEpochSchema);
export const resetGroceryListSchema = z.union([
  z.object({ clearChecked: z.literal(false).optional() }).strict(),
  z.object({ clearChecked: z.literal(true) }).strict(),
  z.object({ clearChecked: z.literal(true), checkoffOperationId: resetCausalOperationSchema, epochsByItemId: epochsByItemIdSchema }).strict(),
]);

export type ResetGroceryListInput = z.infer<typeof resetGroceryListSchema>;

/**
 * Body schema for PUT /meal-plans/:id/slots/:slotId/reschedule (ladder item
 * 12: move a meal to another day instead of regenerating the plan).
 * `.strict()` per the security-boundary pattern above. `date` is a calendar
 * date ("YYYY-MM-DD"), never an instant -- same rationale as
 * createMealPlanSchema.startDate: the server stores it into a @db.Date.
 * `mealType` optional: an omitted value keeps the slot's own mealType (the
 * common "move Wednesday's dinner to Friday" case); when present it must be
 * a real MealType (reuses mealTypeEnum from ./recipe -- one vocabulary, not
 * two).
 */
export const rescheduleMealSlotSchema = z
  .object({
    date: z.string().date(),
    mealType: mealTypeEnum.optional(),
    confirmDependents: z.boolean().optional(),
  })
  .strict();

export type RescheduleMealSlotInput = z.infer<typeof rescheduleMealSlotSchema>;

/**
 * Body of PUT /meal-plans/:id/slots/:slotId/servings (issue #1201): how many
 * servings a planned meal makes. Same 1-100 bound as direct placement.
 */
export const updateMealSlotServingsSchema = z
  .object({
    servings: z.number().int().min(1).max(100),
  })
  .strict();

export type UpdateMealSlotServingsInput = z.infer<
  typeof updateMealSlotServingsSchema
>;

export type UserPreferences = z.infer<typeof userPreferencesSchema>;
export type CreateMealPlanInput = z.infer<typeof createMealPlanSchema>;
export type ResolveAllergiesInput = z.infer<typeof resolveAllergiesSchema>;
export type RateRecipeInput = z.infer<typeof rateRecipeSchema>;

// ── Households ──────────────────────────────────────────────────────────────

/** Hard cap on household membership, enforced by the join route. */
export const MAX_HOUSEHOLD_SIZE = 6;

export const joinHouseholdSchema = z.object({
  token: z.string().trim().min(1, "token is required"),
});

export type JoinHouseholdInput = z.infer<typeof joinHouseholdSchema>;

// ── Closed beta ─────────────────────────────────────────────────────────────

/** A beta credential presented at /beta/claim before registration. */
export const betaClaimSchema = z.object({
  type: z.enum(["code", "household"]),
  value: z.string().trim().min(1, "value is required").max(200),
});

export type BetaClaimInput = z.infer<typeof betaClaimSchema>;

/**
 * Admin batch generation of beta invite codes.
 *
 * `count`'s max(100) mirrors MAX_CODES_PER_RUN in
 * apps/api/src/services/beta-invites.ts. validators must not import from an
 * app, so the two are kept in sync by hand — change one, change the other.
 */
export const generateBetaInvitesSchema = z.object({
  count: z.number().int().min(1).max(100),
  maxUses: z.number().int().min(1).max(1000).optional(),
  note: z.string().trim().max(200).optional(),
  expiresInDays: z.number().int().positive().max(365).optional(),
});

export type GenerateBetaInvitesInput = z.infer<typeof generateBetaInvitesSchema>;

// Export recipe schemas
export * from "./recipe";
export * from "./recipe-import";
export * from "./pantry";
export * from "./pantry-intake";
export * from "./prep-tasks";
export * from "./cooked-batches";
export * from "./feedback";
export * from "./pmf-survey";

/** Direct, add-only recipe placement; null means the caller observed no week. */
export const placeMealSchema = z.object({
  operationId: z.string().uuid(),
  expectedPlanId: z.string().min(1).nullable(),
  date: z.string().date(),
  mealType: mealTypeEnum,
  recipeId: z.string().min(1),
  proteinChoiceId: z.string().trim().min(1).max(100).nullable().optional(),
  servings: z.number().int().min(1).max(100),
  // Requests the stricter recommendation-content gate, not trusted attribution.
  source: z.literal("suggestion").optional(),
}).strict();
export type PlaceMealInput = z.infer<typeof placeMealSchema>;

export * from "./recommendation-feedback";
export * from "./recommendation-attribution";
